import React, { useState, useRef, useEffect } from 'react'
import { usePopper } from 'react-popper'
import { Placement } from '@popperjs/core'
import './Tooltip.css'

interface TooltipProps {
    content: React.ReactNode
    children: React.ReactElement
    placement?: Placement
    delay?: number
    offset?: [number, number]
    className?: string
    variant?: 'default' | 'error'
}

export const Tooltip: React.FC<TooltipProps> = ({
    content,
    children,
    placement = 'top',
    delay = 200,
    offset = [0, 8],
    className = '',
    variant = 'default',
}) => {
    const [isVisible, setIsVisible] = useState(false)
    const [referenceElement, setReferenceElement] = useState<HTMLElement | null>(null)
    const [popperElement, setPopperElement] = useState<HTMLDivElement | null>(null)
    const [arrowElement, setArrowElement] = useState<HTMLDivElement | null>(null)
    const timeoutRef = useRef<NodeJS.Timeout | null>(null)

    const { styles, attributes } = usePopper(referenceElement, popperElement, {
        placement,
        modifiers: [
            {
                name: 'arrow',
                options: {
                    element: arrowElement,
                },
            },
            {
                name: 'offset',
                options: {
                    offset,
                },
            },
            {
                name: 'preventOverflow',
                options: {
                    padding: 8,
                },
            },
        ],
    })

    const showTooltip = () => {
        timeoutRef.current = setTimeout(() => {
            setIsVisible(true)
        }, delay)
    }

    const hideTooltip = () => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current)
        }
        setIsVisible(false)
    }

    useEffect(() => {
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current)
            }
        }
    }, [])

    const handleMouseEnter = (e: React.MouseEvent) => {
        showTooltip()
        if (children.props.onMouseEnter) {
            children.props.onMouseEnter(e)
        }
    }

    const handleMouseLeave = (e: React.MouseEvent) => {
        hideTooltip()
        if (children.props.onMouseLeave) {
            children.props.onMouseLeave(e)
        }
    }

    const handleFocus = (e: React.FocusEvent) => {
        showTooltip()
        if (children.props.onFocus) {
            children.props.onFocus(e)
        }
    }

    const handleBlur = (e: React.FocusEvent) => {
        hideTooltip()
        if (children.props.onBlur) {
            children.props.onBlur(e)
        }
    }

    return (
        <>
            <span
                ref={setReferenceElement}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                onFocus={handleFocus}
                onBlur={handleBlur}
            >
                {children}
            </span>
            {isVisible && (
                <div
                    ref={setPopperElement}
                    style={{
                        ...styles.popper,
                        zIndex: 9999,
                    }}
                    {...attributes.popper}
                    className={`tooltip-container tooltip-${variant} ${className}`}
                >
                    <div className="tooltip-content">
                        {content}
                    </div>
                    <div
                        ref={setArrowElement}
                        style={styles.arrow}
                        className="tooltip-arrow"
                    />
                </div>
            )}
        </>
    )
}
